import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { toast } from 'sonner';
import { ConditionalAccess } from '../../../../../admin/components/ConditionalAccess';
import { IAdminUserSessionComponentProps } from '../../../../../models/AdminModels';
import {
	FullScreenModalViewModelKey,
	IFullscreenModalComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { copyToClipboard } from '../../../../../models/UiUtils';
import { ContactViewModel } from '../../../../../viewmodels/AppViewModels';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { getAutomationReportCreator, getAutomationReportSubject } from '../../../../utils/automationReportHelpers';
import { Contacts } from '../../../../containers/reporting/Campaigns/Contacts';
import { baseStyleSheet } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { ClipboardIcon } from '../../../svgs/icons/ClipboardIcon';
import { styleSheet as groupStyleSheet } from '../GroupEmailTableRowExpandable/styles';
import { GroupEmailTableRowStatusDisplay } from '../GroupEmailTableRowStatus/GroupEmailTableRowStatusDisplay';
import { getCampaignStatusValues } from '../GroupEmailTableRowStatus/utils';
import { GroupEmailTags } from '../GroupEmailTags';
import { styleSheet } from './styles';

export interface IGroupEmailTableRowCallbacks {
	onCancelAutomationClick?(e: React.MouseEvent<HTMLElement>): void;
	onCancelAutomationStepClick?(e: React.MouseEvent<HTMLElement>): void;
	onCheckChanged?: (checked: boolean) => void;
	onClick?(e: React.MouseEvent<HTMLElement>): void;
	onManageAutomationClick?(e: React.MouseEvent<HTMLElement>): void;
	onSubjectClicked?(e: React.MouseEvent<HTMLElement>): void;
	isLastRow?: boolean;
}

interface IProps
	extends IGroupEmailTableRowCallbacks,
		IAdminUserSessionComponentProps,
		RouteComponentProps<any>,
		IFullscreenModalComponentProps {
	automationReport: Api.IAutomationReport;
	className?: string;
	isGroupItem?: boolean;
	preventAutomationFunctionality?: boolean;
	styles?: StyleDeclarationValue[];
}

class _GroupEmailTableAutomationRow extends React.PureComponent<IProps> {
	state: { checked: boolean; sampleContactViewModels: ContactViewModel[] } = {
		checked: false,
		sampleContactViewModels: [],
	};

	constructor(props: IProps) {
		super(props);
		this.state = {
			checked: false,
			sampleContactViewModels: (props.automationReport.contacts || []).map(
				contact => new ContactViewModel(props.userSession, contact)
			),
		};
	}

	handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.stopPropagation();
		const checked = e.target.checked;
		this.setState(
			{
				checked,
			},
			() => {
				this.props.onCheckChanged(checked);
			}
		);
	};

	public render() {
		const { automationReport, className, onClick, styles, onCheckChanged, isLastRow } = this.props;
		const elementDataProps =
			!import.meta.env.PROD && automationReport
				? {
						['data-id']: automationReport.id || '',
					}
				: {};

		return (
			<div
				className={`group-email-table-row ${css(isLastRow ? styleSheet.rowLast : styleSheet.row, ...(styles || []))} ${className || ''}`}
				onClick={onClick}
				{...elementDataProps}
			>
				{onCheckChanged ? (
					<div className={css(styleSheet.checkboxCol)}>
						<Checkbox
							id={`campaign-checkbox-${automationReport.id}`}
							checked={this.state.checked}
							onChange={this.handleCheckChange}
							onClick={e => {
								e.stopPropagation();
							}}
						/>
					</div>
				) : null}
				{this.renderSubject()}
				{this.renderSentToCol()}
				{this.renderAutomationActionsColumn()}
			</div>
		);
	}

	private onRenderTagsFallback = () => {
		if (this.state.sampleContactViewModels?.length) {
			return this.renderContacts();
		}
		return null;
	};

	private renderAutomationActionsColumn = () => {
		const {
			onCancelAutomationClick,
			onCancelAutomationStepClick,
			onManageAutomationClick,
			preventAutomationFunctionality,
		} = this.props;

		if (!preventAutomationFunctionality) {
			return (
				<div className={css(styleSheet.automationCol)}>
					<div className={css(styleSheet.automationColLinkWrapper)}>
						<button className={`${css(baseStyleSheet.brandLink, styleSheet.button)}`} onClick={onCancelAutomationClick}>
							<span className={css(baseStyleSheet.whiteSpaceNoWrap)}>Cancel Automation</span>
						</button>
						<button
							className={`${css(baseStyleSheet.brandLink, styleSheet.button)}`}
							onClick={onCancelAutomationStepClick}
						>
							<span className={css(baseStyleSheet.whiteSpaceNoWrap)}>Cancel Step</span>
						</button>
					</div>
					<div className={css(styleSheet.automationColButtonWrapper)}>
						<button
							className={`${css(baseStyleSheet.ctaButton, styleSheet.button, styleSheet.manageAutomationCTA)}`}
							onClick={onManageAutomationClick}
						>
							<span>Manage</span>
						</button>
					</div>
				</div>
			);
		}

		return null;
	};

	private renderContacts() {
		return (
			<div className={css(styleSheet.contacts)}>
				<Contacts
					maxContacts={3}
					totalContacts={this.state.sampleContactViewModels.length}
					contacts={this.state.sampleContactViewModels}
				/>
			</div>
		);
	}

	private renderSentToCol() {
		return <GroupEmailTags onRenderFallback={this.onRenderTagsFallback} styles={[styleSheet.tags]} />;
	}

	private renderStatus = () => {
		const { automationReport } = this.props;
		if (!automationReport.status) {
			return null;
		}
		const values = getCampaignStatusValues(automationReport);
		const creator = getAutomationReportCreator(automationReport);
		return (
			<GroupEmailTableRowStatusDisplay
				color={values.colorStyle}
				date={new Date(values.dateString)}
				datePrefix={values.datePrefix}
				status={values.statusText}
				userRef={creator}
				styles={[styleSheet.status]}
			/>
		);
	};

	private renderSubject = () => {
		const { automationReport, isGroupItem, onSubjectClicked, preventAutomationFunctionality } = this.props;
		const subject = getAutomationReportSubject(automationReport);

		return (
			<div className={css(styleSheet.subjectCell, isGroupItem ? groupStyleSheet.groupItemSubject : null)}>
				<div className={css(styleSheet.subject)} onClick={onSubjectClicked}>
					<div
						className={css(
							baseStyleSheet.brandLink,
							baseStyleSheet.truncateText,
							preventAutomationFunctionality && styleSheet.unClickableSubject
						)}
					>
						{subject}
					</div>
					<ConditionalAccess permission={AdminPermission.ReadAccount}>
						<button
							onClick={e => {
								e.stopPropagation();
								copyToClipboard(automationReport?.id ?? '');
								toast.success('ID copied to clipboard');
							}}
							className={css(styleSheet.copyIdIcon)}
						>
							<ClipboardIcon className={css(styleSheet.linkIcon)} />
						</button>
					</ConditionalAccess>
				</div>
				<span className={css(styleSheet.automationSteps)}>
					{`Step ${automationReport.stepIndex + 1}/${automationReport.stepCount} of Automation: `}
					<span className={css(styleSheet.automationName)}>{automationReport.name}</span>
				</span>
				{this.renderStatus()}
			</div>
		);
	};
}

const GroupEmailTableAutomationRowAsObserver = observer(_GroupEmailTableAutomationRow);
const GroupEmailTableAutomationRowWithRouter = withRouter(GroupEmailTableAutomationRowAsObserver);
export const GroupEmailTableAutomationRow = inject(
	UserSessionViewModelKey,
	FullScreenModalViewModelKey
)(GroupEmailTableAutomationRowWithRouter);
